import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import {
    Modal, Form, Input, Select, InputNumber,
    Switch, Row, Col, DatePicker
} from 'antd';
import { CarOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../../variables/api/axiosInstance';
import { toast } from 'react-toastify';

const { Option } = Select;
const { TextArea } = Input;

/* ─── Listes statiques (à adapter selon votre contexte) ─────────────── */
const listeDesCategoriesDePermis = [
    { label: 'A', value: 'A' }, { label: 'A1', value: 'A1' },
    { label: 'B', value: 'B' }, { label: 'B1', value: 'B1' },
    { label: 'C', value: 'C' }, { label: 'C1', value: 'C1' },
    { label: 'D', value: 'D' }, { label: 'D1', value: 'D1' },
    { label: 'E', value: 'E' }, { label: 'F', value: 'F' },
    { label: 'G', value: 'G' },
];

const cityOptions = [
    { label: 'Lomé', value: 'Lomé' },
    { label: 'Sokodé', value: 'Sokodé' },
    { label: 'Kara', value: 'Kara' },
    { label: 'Atakpamé', value: 'Atakpamé' },
    { label: 'Kpalimé', value: 'Kpalimé' },
    { label: 'Tsévié', value: 'Tsévié' },
    { label: 'Aného', value: 'Aného' },
    { label: 'Dapaong', value: 'Dapaong' },
];

/* ─── Composant ─────────────────────────────────────────────────────── */
const AddAdversaire = (props) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [typeVictime, setTypeVictime] = useState('vehicule');
    const [typeSerie, setTypeSerie] = useState('TG');
    const [typeSinistreAdversaire, setTypeSinistreAdversaire] = useState(null);
    const [conducteurEstVictime, setConducteurEstVictime] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [marques, setMarques] = useState([]);
    const [assuranceOptions, setAssuranceOptions] = useState([]);

    const estVehicule = typeVictime?.startsWith('vehicule');
    const estPieton = typeVictime === 'pieton';
    const estAutre = typeVictime === 'autre';

    /* ── Chargement des référentiels ── */
    useEffect(() => {
        axiosInstance.get('/api/parametre/type/MARQUE')
            .then((res) => {
                const uniqueMarques = [
                    ...new Map(
                        res.data
                            ?.filter(m => m?.libelleParametre && m.libelleParametre.trim() !== '')
                            .map(m => [m.libelleParametre, m])
                    ).values()
                ];
                setMarques(uniqueMarques);
            })
            .catch(() => toast.error("Erreur lors de la récupération des marques"));

        axiosInstance.get('/api/organisme/assurance').then(res => {
            const opts = (Array.isArray(res.data) ? res.data : [])
                .map(it => ({ value: it?.raisonSociale, label: it?.raisonSociale }))
                .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
            setAssuranceOptions(opts);
        }).catch(() => setAssuranceOptions([]));

    }, []);

    /* ── Initialisation / réinitialisation à l'ouverture ── */
    useEffect(() => {
        if (!props.open) {
            form.resetFields();
            setTypeVictime('vehicule');
            setTypeSerie('TG');
            setTypeSinistreAdversaire(null);
            setConducteurEstVictime(true);
            setEditMode(false);
            return;
        }

        if (props.adversaire) {
            const { adversaire } = props;
            const personne = adversaire.personne || {};
            const engin = adversaire.engin || null;
            const driver = adversaire.driver || null;

            setEditMode(true);
            setTypeVictime(engin ? 'vehicule' : adversaire?.objet ? 'autre' : 'pieton');
            setConducteurEstVictime(!driver);
            setTypeSinistreAdversaire(adversaire.typeSinistre || null);

            form.setFieldsValue({
                ...adversaire,
                typeVictime: engin ? 'vehicule' : adversaire?.objet ? 'autre' : 'pieton',
                personne,
                engin: engin || {},
                driver: driver || {},
                typeSerie: 'TG',
            });
        } else {
            setEditMode(false);
            setTypeVictime('vehicule');
            setTypeSerie('TG');
            setTypeSinistreAdversaire(null);
            setConducteurEstVictime(true);
            form.resetFields();
            form.setFieldsValue({
                adversaire: true,
                conducteur: true,
                typeVictime: 'vehicule',
                typeSerie: 'TG',
            });
        }
    }, [props.open, props.adversaire, form]);

    /* ── Soumission ── */
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                ...values,
                adversaire: true,
                conducteur: estVehicule ? conducteurEstVictime : null,
                engin: estVehicule ? values.engin : null,
                driver: estVehicule && !conducteurEstVictime ? values.driver : null,
                nombrePersonneTransportees: estVehicule ? values.nombrePersonneTransportees : null,
                provenance: estVehicule ? values.provenance : null,
                destination: estVehicule ? values.destination : null,
                vitesse: estVehicule ? values.vitesse : null,
                sinistreCodeId: props.sinistre?.codeId,
                codeId: props.adversaire?.codeId,
            };

            const response = editMode
                ? await axiosInstance.put(`api/victime/adversaire`, JSON.stringify(payload))
                : await axiosInstance.post(`api/victime/adversaire`, JSON.stringify(payload));

            toast(editMode ? 'Adversaire modifié avec succès !' : 'Adversaire ajouté avec succès !');
            props.onSaved && props.onSaved(response.data);
            props.setOpen && props.setOpen(false);
            props.onClose && props.onClose();
            window.location.reload();

        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            if (!error?.errorFields) {
                toast.error("Une erreur est survenue lors de l'enregistrement de la partie adverse.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        props.setOpen && props.setOpen(false);
        props.onClose && props.onClose();
    };

    /* ── Rendu ── */
    return (
        <Modal
            title={editMode ? "Modifier la Partie Adverse" : "Ajouter une Partie Adverse"}
            open={props.open}
            onCancel={handleCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            width={1100}
            okText={editMode ? "Modifier" : "Ajouter"}
            cancelText="Annuler"
            zIndex={2000}
            closable={false}
            maskClosable={false}
        >
            <Form form={form} layout="vertical">

                {/* ── Type de partie adverse ── */}
                <Form.Item
                    label="Type d'adversaire"
                    name="typeVictime"
                    initialValue="vehicule"
                >
                    <Select
                        style={{ width: '100%' }}
                        onChange={(v) => {
                            setTypeVictime(v);
                            setTypeSinistreAdversaire(null);
                            form.setFieldsValue({ typeSinistre: undefined });
                        }}
                    >
                        <Option value="pieton">🚶 Piéton</Option>
                        <Option value="vehicule">🚗 Véhiculé</Option>
                        <Option value="vehicule_moto">🏍️ Véhiculé – Moto (2 roues)</Option>
                        <Option value="vehicule_tricycle">🛺 Véhiculé – Tricycle (3 roues)</Option>
                        <Option value="autre">⚠️ Autre (Objet, Mur, etc.)</Option>
                    </Select>
                </Form.Item>

                {/* ── Piéton ── */}
                {estPieton && (
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Nom" name={['personne', 'nom']} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Prénom" name={['personne', 'prenom']} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Téléphone" name={['personne', 'telephone']}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Adresse" name={['personne', 'adresse']}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {/* ── Autre ── */}
                {estAutre && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item label="Nom du propriétaire" name={['personne', 'nom']}>
                                <Input placeholder="Nom ou dénomination sociale" />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="Description / Objet" name="objet" rules={[{ required: true }]}>
                                <Input placeholder="Ex : Mur, Poteau, Animal…" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {/* ── Véhiculé ── */}
                {estVehicule && (
                    <>
                        <Row gutter={16}>
                            {/* Identité */}
                            <Col span={6}>
                                <Form.Item label="Nom" name={['personne', 'nom']}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label="Prénom" name={['personne', 'prenom']}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label="Téléphone" name={['personne', 'telephone']}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label="Adresse" name={['personne', 'adresse']}>
                                    <Input />
                                </Form.Item>
                            </Col>

                            {/* Immatriculation avec sélecteur de série */}
                            <Col span={8}>
                                <Form.Item label="Immatriculation">
                                    <Input.Group compact>
                                        <Form.Item name="typeSerie" noStyle initialValue="TG">
                                            <Select
                                                style={{ width: '25%' }}
                                                onChange={(v) => {
                                                    setTypeSerie(v);
                                                    if (v !== 'TG') {
                                                        form.setFieldsValue({ numeroSerie: undefined });
                                                    }
                                                }}
                                            >
                                                <Option value="TG">TG</Option>
                                                <Option value="AUTRE">Autre</Option>
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            name={['engin', 'immatriculation']}
                                            noStyle
                                            rules={[{ required: true, message: 'Requis' }]}
                                        >
                                            <Input
                                                style={{ width: typeSerie === 'TG' ? '45%' : '75%' }}
                                                placeholder="Ex : 1234 AB"
                                            />
                                        </Form.Item>
                                        {typeSerie === 'TG' && (
                                            <Form.Item
                                                name={['engin', 'numeroSerie']}
                                                noStyle
                                                rules={[{ required: true, message: 'Requis' }]}
                                            >
                                                <Input style={{ width: '30%' }} placeholder="N° série" />
                                            </Form.Item>
                                        )}
                                    </Input.Group>
                                </Form.Item>
                            </Col>

                            {/* Marque */}
                            <Col span={4}>
                                <Form.Item label="Marque" name={['engin', 'marque']}>
                                    <Select
                                        showSearch
                                        options={marques.map(m => ({
                                            key: m.parametreId,
                                            label: m.libelleParametre,
                                            value: m.libelleParametre,
                                        }))}
                                    />
                                </Form.Item>
                            </Col>

                            {/* Modèle */}
                            <Col span={4}>
                                <Form.Item label="Modèle" name={['engin', 'modele']}>
                                    <Input />
                                </Form.Item>
                            </Col>

                            {/* Nombre de personnes */}
                            <Col span={4}>
                                <Form.Item label="Nbre pers." name="nombrePersonneTransportees">
                                    <InputNumber min={0} max={50} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>

                            {/* Vitesse */}
                            <Col span={4}>
                                <Form.Item label="Vitesse (km/h)" name="vitesse">
                                    <InputNumber min={0} max={300} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>

                            {/* Provenance / Destination */}
                            <Col span={12}>
                                <Form.Item label="Venant de" name="provenance">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Se dirigeant vers" name="destination">
                                    <Input />
                                </Form.Item>
                            </Col>

                            {/* Type de dommages */}
                            <Col span={12}>
                                <Form.Item
                                    label="Type de dommages subis"
                                    name="typeSinistre"
                                    rules={[{ required: true, message: 'Sélectionnez le type de dommage' }]}
                                >
                                    <Select
                                        style={{ width: '100%' }}
                                        onChange={setTypeSinistreAdversaire}
                                        placeholder="Sélectionner"
                                    >
                                        <Option value="MATERIEL">🔧 Matériel</Option>
                                        <Option value="CORPOREL">🏥 Corporel</Option>
                                        <Option value="MIXTE">⚠️ Mixte</Option>
                                        <Option value="AUCUN">✅ Aucun</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            {/* Société d'assurance adverse */}
                            <Col span={12}>
                                <Form.Item label="Société d'assurance adverse" name="societeAssurance">
                                    <Select
                                        style={{ width: '100%' }}
                                        options={assuranceOptions}
                                        placeholder="Sélectionner"
                                        showSearch
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Description dégâts matériels */}
                        {(typeSinistreAdversaire === 'MATERIEL' || typeSinistreAdversaire === 'MIXTE') && (
                            <Form.Item
                                label={<b>🔧 Description des dégâts matériels</b>}
                                name="descriptionDegatsMateriels"
                                rules={[{ required: true }]}
                            >
                                <TextArea rows={3} placeholder="Décrire les dommages matériels…" />
                            </Form.Item>
                        )}

                        {/* Description dommages corporels */}
                        {(typeSinistreAdversaire === 'CORPOREL' || typeSinistreAdversaire === 'MIXTE') && (
                            <Form.Item
                                label={<b>🏥 Description des dommages corporels</b>}
                                name="descriptionDegatsCorporels"
                                rules={[{ required: true }]}
                            >
                                <TextArea rows={3} placeholder="Décrire les dommages corporels…" />
                            </Form.Item>
                        )}

                        {/* Switch conducteur */}
                        <Form.Item label={<b>La partie adverse était-elle le conducteur ?</b>}>
                            <Switch
                                checked={conducteurEstVictime}
                                onChange={(v, e) => { e.stopPropagation(); setConducteurEstVictime(v); }}
                                checkedChildren="Oui"
                                unCheckedChildren="Non"
                                style={{ background: conducteurEstVictime ? '#52c41a' : '#d9d9d9' }}
                            />
                        </Form.Item>

                        {/* Section permis */}
                        <div style={{
                            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                            padding: 16,
                            borderRadius: 6,
                            marginTop: 8,
                        }}>
                            <h4 style={{ color: '#1565c0', marginBottom: 12 }}>
                                <CarOutlined style={{ marginRight: 8 }} />
                                Permis {conducteurEstVictime ? 'de la partie adverse' : 'du conducteur adverse'}
                            </h4>

                            {/* Champs conducteur si différent de la partie adverse */}
                            {!conducteurEstVictime && (
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label={<b>Nom</b>} name={['driver', 'nom']} rules={[{ required: true }]}>
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label={<b>Prénom</b>} name={['driver', 'prenom']} rules={[{ required: true }]}>
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label={<b>Téléphone</b>} name={['driver', 'telephone']}>
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label={<b>Adresse</b>} name={['driver', 'adresse']}>
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}

                            {/* Permis commun */}
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={<b>N° permis</b>} name={['driver', 'numeroPermis']}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={<b>Catégorie</b>} name={['driver', 'categoriePermis']}>
                                        <Select options={listeDesCategoriesDePermis} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={<b>Date d'obtention</b>} name={['driver', 'dateObtentionPermis']}>
                                        <DatePicker
                                            disabledDate={c => c && c > new Date()}
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={<b>Lieu d'obtention</b>} name={['driver', 'lieuObtentionPermis']}>
                                        <Select options={cityOptions} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </>
                )}
            </Form>
        </Modal>
    );
};

AddAdversaire.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    adversaire: PropTypes.object,
    sinistre: PropTypes.object,
    onClose: PropTypes.func,
    onSaved: PropTypes.func,
};

export default AddAdversaire;